import React from 'react';
import gameSessionsApi, { configureGamesApi, gameErrorMessage } from '../../utils/gameSessionsApi';
import { watchGameSession } from '../../utils/gameSessionRealtime';
import NewGame from './NewGame';
import GameLobby from './GameLobby';
import GameComplete from './GameComplete';
import Countdown from './Countdown';
import MatchingGame from './MatchingGame';
import { FunButton, GameModal, Spinner } from './GameUI';
import ProModal from '../app_components/ProModal';

const IN_PLAY = ['onboarding', 'playing'];
const ENDED = ['complete', 'abandoned'];
const POLL_WITH_REALTIME_MS = 15000;
const POLL_WITHOUT_REALTIME_MS = 3000;
const CORRECT_FEEDBACK_MS = 1500;
const TIMEOUT_RETRY_MS = 4000;

function resultKey(result) {
  return result ? `${result.round}:${result.outcome}:${result.picture_id}` : null;
}

function packFromActivity(activity) {
  return activity
    ? {
      activity_id: activity.id,
      title: activity.title,
      body: activity.body,
      teaser_image_url: activity.teaser_image_url || null,
    }
    : null;
}

class GamesApp extends React.Component {
  constructor(props) {
    super(props);
    configureGamesApi(props.authUrl);

    const languages = props.languages || [];
    this.state = {
      loading: true,
      pack: props.initialPack || null,
      joinOnly: (props.joinOnly === true || !!props.initialCode) && !props.initialPack,
      language: languages.find((l) => l.id === props.defaultLanguageId) || languages[0] || null,
      durationSeconds: 300,
      pictureSet: props.premiumUser ? 'extended' : 'basic',
      pictureSetExplicit: false,
      session: null,
      busy: false,
      error: '',
      joinError: '',
      notice: '',
      countdown: false,
      showCorrect: false,
      confirmEnd: false,
      upgradeOpen: false,
      clockOffsetMs: 0,
      nowMs: Date.now(),
    };

    this.stopWatching = null;
    this.watchedSessionId = null;
    this.realtimeOk = false;
    this.lastTimeoutSentAt = 0;
  }

  componentDidMount() {
    document.addEventListener('visibilitychange', this.onResume);
    window.addEventListener('online', this.onResume);
    this.ticker = setInterval(this.tick, 1000);

    const opening = this.props.initialCode
      ? this.joinGame(this.props.initialCode)
      : this.restoreSession({ resumeLobby: !this.props.initialPack });
    opening.finally(() => {
      if (!this.unmounted) this.setState({ loading: false });
    });
  }

  componentDidUpdate() {
    // The site's bottom tab bar would cover the board; hide it while a game is on screen.
    document.body.classList.toggle('gpPlaying', this.isPlaying());
    this.ensurePackCounts();
  }

  componentWillUnmount() {
    document.body.classList.remove('gpPlaying');
    this.unmounted = true;
    document.removeEventListener('visibilitychange', this.onResume);
    window.removeEventListener('online', this.onResume);
    clearInterval(this.ticker);
    clearTimeout(this.pollTimer);
    clearTimeout(this.correctTimer);
    this.stopSync();
  }

  // --- saved session ---------------------------------------------------------

  get storageKey() {
    return `lexody.activeGameSession.${this.props.myUserId}`;
  }

  storedSessionId() {
    try {
      return window.localStorage.getItem(this.storageKey);
    } catch (error) {
      return null;
    }
  }

  storeSessionId(id) {
    try {
      if (id) window.localStorage.setItem(this.storageKey, String(id));
      else window.localStorage.removeItem(this.storageKey);
    } catch (error) {
      // Private browsing can block storage; rejoining after a reload just won't work.
    }
  }

  async restoreSession({ resumeLobby }) {
    const storedId = this.storedSessionId();
    if (!storedId) return;
    try {
      const snapshot = await gameSessionsApi.get(storedId);
      if (ENDED.includes(snapshot.status) || (snapshot.status === 'lobby' && !resumeLobby)) {
        this.storeSessionId(null);
        return;
      }
      this.applySnapshot(snapshot);
    } catch (error) {
      this.storeSessionId(null);
    }
  }

  // --- session state ---------------------------------------------------------

  applySnapshot = (snapshot) => {
    if (this.unmounted || !snapshot) return;
    const prev = this.state.session;
    if (prev && prev.id === snapshot.id && snapshot.version < prev.version) {
      return;
    }

    const next = { session: snapshot };
    const game = snapshot.game;
    if (game && game.server_now) {
      next.clockOffsetMs = new Date(game.server_now).getTime() - Date.now();
      next.nowMs = Date.now();
    }

    if (prev && prev.id === snapshot.id) {
      if (prev.status === 'lobby' && IN_PLAY.includes(snapshot.status)) {
        next.countdown = true;
      }
      const prevResult = prev.game && prev.game.last_result;
      const result = game && game.last_result;
      if (result && result.outcome === 'correct' && resultKey(result) !== resultKey(prevResult)) {
        next.showCorrect = true;
        clearTimeout(this.correctTimer);
        this.correctTimer = setTimeout(() => this.setState({ showCorrect: false }), CORRECT_FEEDBACK_MS);
      }
    } else {
      this.clearUrlParams();
    }

    if (snapshot.status === 'abandoned' && prev && prev.status !== 'abandoned') {
      next.notice = snapshot.host_user_id === this.props.myUserId ? '' : 'The host cancelled this game.';
    }

    this.setState(next);
    this.syncWatcher(snapshot);
  };

  // A reload should resume the saved game, not re-run ?code= or ?pack= from the link.
  clearUrlParams() {
    if (window.location.search) {
      window.history.replaceState(window.history.state, '', window.location.pathname);
    }
  }

  refresh = async () => {
    const session = this.state.session;
    if (!session) return;
    try {
      this.applySnapshot(await gameSessionsApi.get(session.id));
    } catch (error) {
      console.log('[Games] refresh failed', gameErrorMessage(error));
    }
  };

  // --- live updates ----------------------------------------------------------

  syncWatcher(snapshot) {
    if (ENDED.includes(snapshot.status)) {
      this.storeSessionId(null);
      this.stopSync();
      return;
    }
    if (this.watchedSessionId === snapshot.id) return;

    this.stopSync();
    this.storeSessionId(snapshot.id);
    this.watchedSessionId = snapshot.id;
    this.realtimeOk = false;
    this.stopWatching = watchGameSession(snapshot.id, {
      onChange: (row) => {
        const current = this.state.session;
        if (!current || (row && row.version !== undefined && row.version <= current.version)) return;
        this.refresh();
      },
      onStatus: (ok) => {
        const wasOk = this.realtimeOk;
        this.realtimeOk = ok;
        // Catch anything missed while the channel was down.
        if (ok && !wasOk) this.refresh();
        this.schedulePoll();
      },
    });
    this.schedulePoll();
  }

  schedulePoll() {
    clearTimeout(this.pollTimer);
    if (!this.watchedSessionId) return;
    const delay = this.realtimeOk ? POLL_WITH_REALTIME_MS : POLL_WITHOUT_REALTIME_MS;
    this.pollTimer = setTimeout(async () => {
      if (document.visibilityState === 'visible') {
        await this.refresh();
      }
      this.schedulePoll();
    }, delay);
  }

  stopSync() {
    clearTimeout(this.pollTimer);
    if (this.stopWatching) {
      this.stopWatching();
      this.stopWatching = null;
    }
    this.watchedSessionId = null;
    this.realtimeOk = false;
  }

  onResume = () => {
    if (document.visibilityState !== 'visible' || !this.watchedSessionId) return;
    const sessionId = this.watchedSessionId;
    // A sleeping laptop or dropped network can leave the socket dead without an error.
    this.stopSync();
    const session = this.state.session;
    if (session && session.id === sessionId) {
      this.syncWatcher(session);
    }
    this.refresh();
  };

  // --- clock -----------------------------------------------------------------

  tick = () => {
    const session = this.state.session;
    if (!session || session.status !== 'playing') return;
    this.setState({ nowMs: Date.now() });

    const remaining = this.secondsRemaining();
    const now = Date.now();
    if (remaining !== null && remaining <= 0 && now - this.lastTimeoutSentAt >= TIMEOUT_RETRY_MS) {
      this.lastTimeoutSentAt = now;
      this.runAction(() => gameSessionsApi.timeout(session.id), { quiet: true });
    }
  };

  secondsRemaining() {
    const session = this.state.session;
    const endsAt = session && session.game && session.game.ends_at;
    if (!endsAt) return null;
    const serverNow = this.state.nowMs + this.state.clockOffsetMs;
    return Math.max(0, Math.ceil((new Date(endsAt).getTime() - serverNow) / 1000));
  }

  // --- actions ---------------------------------------------------------------

  async runAction(call, { quiet = false } = {}) {
    if (!quiet) this.setState({ busy: true, error: '' });
    try {
      const snapshot = await call();
      this.applySnapshot(snapshot);
      return snapshot;
    } catch (error) {
      if (!quiet) this.setState({ error: gameErrorMessage(error) });
      this.refresh();
      return null;
    } finally {
      if (!quiet && !this.unmounted) this.setState({ busy: false });
    }
  }

  ensurePackCounts() {
    const pack = this.state.pack;
    if (this.state.session || !pack || pack.premium_picture_count !== undefined || this.loadingPackCounts) {
      return;
    }
    this.loadingPackCounts = true;
    gameSessionsApi.packs()
      .then((packs) => {
        if (this.unmounted) return;
        const match = (packs || []).find((item) => item.activity_id === pack.activity_id);
        this.setState((state) => {
          if (!state.pack || state.pack.activity_id !== pack.activity_id) return null;
          if (match) return { pack: { ...state.pack, ...match } };
          return { pack: { ...state.pack, premium_picture_count: 0, basic_picture_count: state.pack.picture_count || 0 } };
        });
      })
      .catch(() => {
        if (this.unmounted) return;
        this.setState((state) => (
          state.pack && state.pack.premium_picture_count === undefined
            ? { pack: { ...state.pack, premium_picture_count: 0 } }
            : null
        ));
      })
      .finally(() => { this.loadingPackCounts = false; });
  }

  createGame = () => {
    const { pack, language, durationSeconds, pictureSet, pictureSetExplicit } = this.state;
    if (!pack || !language) {
      this.setState({ error: 'Choose a game and a language first.' });
      return;
    }
    this.setState({ notice: '' });
    this.runAction(() => gameSessionsApi.create({
      activity_id: pack.activity_id,
      language_id: language.id,
      duration_seconds: durationSeconds,
      picture_set: pictureSet,
      picture_set_explicit: pictureSetExplicit,
    }));
  };

  setPictureSet = (pictureSet) => {
    const session = this.state.session;
    if (!session) {
      this.setState({ pictureSet, pictureSetExplicit: true });
      return;
    }
    this.runAction(() => gameSessionsApi.setPictureSet(session.id, pictureSet));
  };

  upgrade = () => {
    this.setState({ upgradeOpen: true });
  };

  joinGame = async (code) => {
    this.setState({ busy: true, joinError: '', notice: '' });
    try {
      this.applySnapshot(await gameSessionsApi.join(code));
    } catch (error) {
      this.setState({ joinError: gameErrorMessage(error) });
    } finally {
      if (!this.unmounted) this.setState({ busy: false });
    }
  };

  startGame = ({ practice }) => {
    this.runAction(() => gameSessionsApi.start(this.state.session.id, { practice }));
  };

  endPractice = () => {
    this.runAction(() => gameSessionsApi.endPractice(this.state.session.id));
  };

  guess = (position) => {
    if (this.state.busy) return;
    this.runAction(() => gameSessionsApi.guess(this.state.session.id, position));
  };

  confirmEndGame = () => {
    this.setState({ confirmEnd: false });
    this.runAction(() => gameSessionsApi.complete(this.state.session.id));
  };

  leaveLobby = async () => {
    const session = this.state.session;
    if (session && session.host_user_id === this.props.myUserId) {
      await this.runAction(() => gameSessionsApi.complete(session.id));
    }
    this.resetGame();
  };

  resetGame = ({ keepPack = true } = {}) => {
    this.stopSync();
    this.storeSessionId(null);
    const session = this.state.session;
    const pack = keepPack
      ? this.state.pack || packFromActivity(session && session.activity)
      : null;
    this.setState({
      session: null,
      pack,
      joinOnly: pack ? false : this.state.joinOnly && keepPack,
      countdown: false,
      showCorrect: false,
      error: '',
      joinError: '',
    });
  };

  // --- render ----------------------------------------------------------------

  renderBody() {
    const { session } = this.state;

    if (this.state.loading) {
      return <div className="gpLoading"><Spinner /></div>;
    }

    if (!session || session.status === 'abandoned') {
      return (
        <NewGame
          pack={this.state.pack}
          joinOnly={this.state.joinOnly}
          languages={this.props.languages || []}
          userLanguages={this.props.userLanguages || []}
          language={this.state.language}
          durationSeconds={this.state.durationSeconds}
          pictureSet={this.state.pictureSet}
          premiumUser={this.props.premiumUser === true}
          busy={this.state.busy}
          error={this.state.error}
          joinError={this.state.joinError}
          notice={this.state.notice}
          initialCode={this.props.initialCode}
          onChoosePack={(pack) => this.setState({ pack, error: '' })}
          onClearPack={() => this.setState({ pack: null, error: '' })}
          onLanguage={(language) => this.setState({ language })}
          onDuration={(durationSeconds) => this.setState({ durationSeconds })}
          onPictureSet={this.setPictureSet}
          onUpgrade={this.upgrade}
          onCreate={this.createGame}
          onJoin={this.joinGame}
        />
      );
    }

    if (session.status === 'lobby') {
      return (
        <GameLobby
          session={session}
          myUserId={this.props.myUserId}
          premiumUser={this.props.premiumUser === true}
          busy={this.state.busy}
          error={this.state.error}
          onStart={this.startGame}
          onPictureSet={this.setPictureSet}
          onUpgrade={this.upgrade}
          onLeave={this.leaveLobby}
        />
      );
    }

    if (session.status === 'complete') {
      return (
        <GameComplete
          title={session.activity ? session.activity.title : ''}
          score={session.game ? session.game.team_score : 0}
          endReason={session.game ? session.game.end_reason : null}
          lotties={this.props.lotties}
          onPlayAgain={() => this.resetGame()}
          onBackToGames={() => this.resetGame({ keepPack: false })}
        />
      );
    }

    if (this.state.countdown) {
      return (
        <Countdown
          activity={session.activity || {}}
          onDone={() => this.setState({ countdown: false })}
        />
      );
    }

    return (
      <MatchingGame
        session={session}
        secondsRemaining={this.secondsRemaining()}
        busy={this.state.busy}
        error={this.state.error}
        showCorrect={this.state.showCorrect}
        lotties={this.props.lotties}
        onGuess={this.guess}
        onEndPractice={this.endPractice}
        onEndGame={() => this.setState({ confirmEnd: true })}
      />
    );
  }

  isPlaying() {
    const { session, loading, countdown } = this.state;
    return !loading && !countdown && !!session && IN_PLAY.includes(session.status) && !!session.game;
  }

  // Screens that switch to a two-column layout on desktop (.gpSplit).
  isWide() {
    const { session, loading, countdown } = this.state;
    if (loading || countdown) return false;
    if (!session || session.status === 'abandoned') return true;
    if (session.status === 'lobby') return session.host_user_id === this.props.myUserId;
    return session.status === 'complete';
  }

  render() {
    const playing = this.isPlaying();
    let modifier = '';
    if (playing) modifier = ' gpPage--play';
    else if (this.isWide()) modifier = ' gpPage--wide';
    return (
      <div className={`gpPage${modifier}`}>
        <div className="gpPage__inner">
          {this.renderBody()}
        </div>
        {this.state.upgradeOpen && (
          <ProModal
            type="extended"
            premiumImage={this.props.premiumImage}
            onClose={() => this.setState({ upgradeOpen: false })}
          />
        )}
        {this.state.confirmEnd && (
          <GameModal title="End the game?" onClose={() => this.setState({ confirmEnd: false })}>
            <p className="gpSubtitle gpModal__text">This ends the game for both of you.</p>
            <FunButton
              variant="primary"
              emoji="🏁"
              text="End game"
              onClick={this.confirmEndGame}
            />
            <FunButton
              variant="ghost"
              text="Keep playing"
              onClick={() => this.setState({ confirmEnd: false })}
              className="gpSpaceTop"
            />
          </GameModal>
        )}
      </div>
    );
  }
}

export default GamesApp;
